import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ExternalLink, Pencil, Trash2 } from 'lucide-react';

const MAGIC_STAGES = ['brief', 'design_system', 'structure', 'content', 'assets', 'build', 'review', 'deploy'];
const TEMPLATE_STAGES = ['brand', 'pages', 'content', 'render', 'review', 'deploy'];
const CLONE_STAGES = ['reference', 'brand', 'build', 'review', 'deploy'];

const STAGE_SHORT = {
  brief: 'Brief', design_system: 'DS', structure: 'Struct', content: 'Content',
  assets: 'Assets', build: 'Build', review: 'Review', deploy: 'Live',
  brand: 'Brand', pages: 'Pages', render: 'Render', reference: 'Ref',
};
const STAGE_LABELS = {
  brief: 'Brief', design_system: 'Design', structure: 'Structure', content: 'Content',
  assets: 'Assets', build: 'Build', review: 'Review', deploy: 'Deploy',
  brand: 'Brand', pages: 'Pages', render: 'Render', reference: 'Reference',
};

function getStagesForProject(project) {
  if (project.template_mode) return TEMPLATE_STAGES;
  if (project.clone_mode) return CLONE_STAGES;
  return MAGIC_STAGES;
}

const STATUS_CONFIG = {
  deployed: { label: 'Live', color: 'var(--orbis-positive)', bg: 'rgba(52, 211, 153, 0.1)' },
  building: { label: 'In Progress', color: 'var(--orbis-blue)', bg: 'rgba(184, 204, 223, 0.1)' },
  needs_action: { label: 'Needs Action', color: 'var(--orbis-amber)', bg: 'rgba(212, 184, 150, 0.1)', pulse: true },
  draft: { label: 'Draft', color: 'var(--orbis-t4)', bg: 'rgba(82, 82, 91, 0.1)' },
};

function getProjectStatus(project) {
  if (project.status === 'deployed' || project.custom_domain) return 'deployed';
  const ms = project.magic_state || {};
  const completed = ms.completed_stages || [];
  if (ms.generated_code?.html || ms.generated_pages?.length) return 'building';
  if (!ms.current_stage && completed.length === 0 && !ms.brief && !ms.brand_context) return 'draft';
  if (completed.length > 0 || ms.brief || ms.brand_context || ms.design_system) return 'building';
  return 'draft';
}

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function getModeBadge(project) {
  if (project.magic_mode) return { label: 'MAGIC', color: 'var(--orbis-blue)' };
  if (project.clone_mode) return { label: 'CLONE', color: 'var(--orbis-amber)' };
  if (project.template_mode) return { label: 'TEMPLATE', color: 'var(--orbis-green)' };
  return { label: 'CLASSIC', color: 'var(--orbis-t4)' };
}

/**
 * Single project spotlight card with named stage progress bar and hover actions.
 */
function SpotlightCard({ project, index, onNavigate, onDelete }) {
  const prefersReducedMotion = useReducedMotion();
  const status = getProjectStatus(project);
  const statusConfig = STATUS_CONFIG[status];
  const badge = getModeBadge(project);
  const stages = getStagesForProject(project);

  // Derive current stage: use current_stage if set, otherwise infer from completed_stages/data
  const ms = project.magic_state || {};
  let currentStage = ms.current_stage;

  // Deployed projects always resolve to 'deploy' so text/stage show correctly
  if (status === 'deployed') {
    currentStage = 'deploy';
  } else if (!currentStage) {
    const completed = ms.completed_stages || [];
    if (completed.length > 0) {
      // Find the furthest completed stage in this project's stage list
      const validCompleted = completed.filter(s => stages.includes(s));
      if (validCompleted.length > 0) {
        const lastCompleted = [...validCompleted].sort((a, b) => stages.indexOf(b) - stages.indexOf(a))[0];
        const lastIdx = stages.indexOf(lastCompleted);
        currentStage = stages[Math.min(lastIdx + 1, stages.length - 1)] || lastCompleted;
      } else {
        currentStage = stages[0];
      }
    } else if (ms.generated_code?.html || ms.generated_pages?.length || project.generated_html) {
      currentStage = 'review';
    } else if (ms.design_system) {
      currentStage = project.template_mode ? 'pages' : (project.clone_mode ? 'build' : 'structure');
    } else if (ms.brief || ms.brand_context) {
      // Clone: brand filled → next is build. Template/Magic: next design stage
      currentStage = project.template_mode ? 'brand' : (project.clone_mode ? 'build' : 'design_system');
    } else if (project.clone_mode && project.reference_analysis) {
      // Clone: URL analyzed but brand not yet filled → at brand/brief step
      currentStage = stages[1]; // 'brand' (second clone stage)
    } else {
      currentStage = stages[0]; // first stage for this project type
    }
  }
  const currentStageIdx = stages.indexOf(currentStage);
  // If stage not found in this project's stage list, default to 0
  const safeStageIdx = currentStageIdx >= 0 ? currentStageIdx : 0;
  const timeAgo = getTimeAgo(project.updated_at);

  return (
    <motion.div
      initial={prefersReducedMotion ? {} : { opacity: 0, y: 24, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        delay: index * 0.1 + 0.5,
        type: 'spring', stiffness: 300, damping: 30, mass: 0.8,
      }}
      whileHover={{ y: -4, transition: { type: 'spring', stiffness: 400, damping: 25 } }}
      onClick={() => onNavigate(project)}
      className="cursor-pointer group"
      style={{
        background: 'var(--orbis-s1)',
        border: '0.5px solid var(--orbis-s2)',
        borderRadius: 14,
        padding: '20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Hover glow */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0,
        background: `radial-gradient(circle at 50% 0%, ${statusConfig.color}10 0%, transparent 60%)`,
        transition: 'opacity 0.3s ease', pointerEvents: 'none',
      }} className="group-hover:!opacity-100" />

      {/* Hover quick-actions */}
      <div
        className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ zIndex: 3 }}
      >
        {project.custom_domain && (
          <button
            onClick={e => { e.stopPropagation(); window.open(`https://${project.custom_domain}`, '_blank'); }}
            className="flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
            title="Visit site"
            style={{
              width: 28, height: 28, borderRadius: 100,
              background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)',
            }}
          >
            <ExternalLink className="w-3 h-3" style={{ color: 'var(--orbis-t3)' }} />
          </button>
        )}
        <button
          onClick={e => { e.stopPropagation(); onNavigate(project); }}
          className="flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
          title="Edit project"
          style={{
            width: 28, height: 28, borderRadius: 100,
            background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)',
          }}
        >
          <Pencil className="w-3 h-3" style={{ color: 'var(--orbis-t3)' }} />
        </button>
        {onDelete && (
          <button
            onClick={e => { e.stopPropagation(); onDelete(project.id); }}
            className="flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
            title="Delete project"
            style={{
              width: 28, height: 28, borderRadius: 100,
              background: 'var(--orbis-s2)', border: '0.5px solid var(--orbis-s3)',
            }}
          >
            <Trash2 className="w-3 h-3" style={{ color: '#F87171' }} />
          </button>
        )}
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Top: badge + status */}
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <span style={{
            fontSize: 9, fontFamily: 'var(--orbis-mono)', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            padding: '3px 8px', borderRadius: 100,
            background: `${badge.color}15`, color: badge.color,
            border: `0.5px solid ${badge.color}30`,
          }}>
            {badge.label}
          </span>
          <span style={{
            fontSize: 10, fontFamily: 'var(--orbis-mono)', fontWeight: 500,
            padding: '3px 8px', borderRadius: 100,
            background: statusConfig.bg, color: statusConfig.color,
            ...(statusConfig.pulse && !prefersReducedMotion ? { animation: 'spotlight-pulse 2s ease-in-out infinite' } : {}),
          }}>
            {statusConfig.label}
          </span>
        </div>

        {/* Project name */}
        <h3 style={{
          fontSize: 16, fontWeight: 500, color: 'var(--orbis-t1)',
          fontFamily: 'var(--orbis-font)', marginBottom: 4,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {project.name}
        </h3>

        {/* Last activity */}
        <p style={{ fontSize: 11, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-font)', marginBottom: 14 }}>
          {status === 'deployed' || currentStage === 'deploy'
            ? `Deployed · ${timeAgo}`
            : `${STAGE_LABELS[currentStage] || currentStage} stage · ${timeAgo}`
          }
        </p>

        {/* Stage progress bar with milestone labels */}
        <div style={{ marginBottom: 14 }}>
          {/* Bar */}
          <div className="flex items-center gap-0.5" style={{ marginBottom: 4 }}>
            {stages.map((stage, i) => {
              const isComplete = i < safeStageIdx;
              const isCurrent = i === safeStageIdx;
              const isDeployed = status === 'deployed';
              const segColor = isDeployed || isComplete
                ? 'var(--orbis-positive)'
                : isCurrent
                  ? 'var(--orbis-blue)'
                  : 'var(--orbis-s3)';
              return (
                <motion.div
                  key={stage}
                  initial={prefersReducedMotion ? {} : { scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: index * 0.1 + 0.7 + i * 0.04, duration: 0.3 }}
                  style={{
                    flex: 1, height: 3, borderRadius: 2,
                    background: segColor, transformOrigin: 'left',
                    ...(isCurrent && !prefersReducedMotion ? { animation: 'stage-pulse 2s ease-in-out infinite' } : {}),
                  }}
                />
              );
            })}
          </div>
          {/* Milestone labels */}
          <div className="flex items-center justify-between">
            {stages.map((stage, i) => {
              const isComplete = i < safeStageIdx;
              const isCurrent = i === safeStageIdx;
              const isDeployed = status === 'deployed';
              return (
                <span key={stage} style={{
                  fontSize: 7, fontFamily: 'var(--orbis-mono)', fontWeight: 500,
                  letterSpacing: '0.02em', textTransform: 'uppercase',
                  color: isCurrent ? 'var(--orbis-blue)'
                    : (isDeployed || isComplete) ? 'var(--orbis-positive)'
                    : 'var(--orbis-t4)',
                  opacity: (isCurrent || isComplete || isDeployed) ? 1 : 0.5,
                }}>
                  {STAGE_SHORT[stage]}
                </span>
              );
            })}
          </div>
        </div>

        {/* Continue button */}
        <div className="flex items-center gap-1" style={{ fontSize: 12, fontWeight: 500, color: 'var(--orbis-blue)', fontFamily: 'var(--orbis-font)' }}>
          {status === 'deployed' ? 'Open' : 'Continue'}
          <ArrowRight className="w-3 h-3" />
        </div>
      </div>

      <style>{`
        @keyframes spotlight-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes stage-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </motion.div>
  );
}

/**
 * Project Spotlight Strip — 3 most recent/active projects.
 */
export default function ProjectSpotlight({ projects, onNavigateToProject, onViewAll, onDeleteProject }) {
  const spotlightProjects = [...projects]
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 3);

  if (spotlightProjects.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <h2 className="flex items-center gap-2" style={{
          fontSize: 13, fontWeight: 600, color: 'var(--orbis-t2)',
          fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          <span style={{ width: 3, height: 14, borderRadius: 2, background: 'var(--orbis-blue)', flexShrink: 0 }} />
          Your Projects
        </h2>
        <button
          onClick={onViewAll}
          className="flex items-center gap-1 cursor-pointer hover:gap-1.5 transition-all"
          style={{
            background: 'none', border: 'none', padding: 0,
            fontSize: 12, color: 'var(--orbis-t3)', fontFamily: 'var(--orbis-font)',
          }}
        >
          View all <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {spotlightProjects.map((project, i) => (
          <SpotlightCard
            key={project.id}
            project={project}
            index={i}
            onNavigate={onNavigateToProject}
            onDelete={onDeleteProject}
          />
        ))}
      </div>
    </div>
  );
}
